// FILE: src/config/featureFlags.js
//
// Centralized, build-time feature flags for PM Pixel.
//
// Each flag has a safe default below and may be overridden per-environment
// via a REACT_APP_* env var (Netlify build context → CRA inlines at build
// time). Keeping flags here — rather than scattering process.env reads through
// components — means a flag can be referenced from config (e.g. exams.js) and
// from UI with a single import, and toggled in one place.
//
// enablePmiPmocp
//   Gates the PMI-PMOCP™ certification track. When false, PMI-PMOCP is hidden
//   from every cert list / selector / dashboard (getAllExams filters it out),
//   exactly as if it were never registered. When true it is a first-class
//   certification alongside PMP, CPMAI, and PMI-ACP. Default: true.
//   Override: REACT_APP_ENABLE_PMI_PMOCP=false
//
// caseStudiesEnabled
//   Gates the learner-facing Case Study Practice experience (Case Study
//   Question Framework). Default: false — infrastructure ships dark.
//   Override: REACT_APP_CASE_STUDIES_ENABLED=true
//
// caseStudyAdminEnabled
//   Gates the admin Case Studies authoring area independently of the learner
//   flow, so cases can be authored before learners see anything.
//   Default: false. Override: REACT_APP_CASE_STUDY_ADMIN_ENABLED=true
//
// caseStudiesInReadinessEnabled
//   Reserved: whether case-study performance feeds readiness calculations.
//   MUST stay false until the readiness model is deliberately updated and
//   tested (design doc, section 2.11). Unused in Phase 1.
//   Override: REACT_APP_CASE_STUDIES_IN_READINESS=true
//
// METADATA_POOL_CERTS (per-cert list, session-draw stage 3)
//   Certifications whose learner session draw may use the lightweight
//   metadata-pool loader (certBankLoader.ensureCertPool) instead of the
//   full-content bank load. Comma-separated cert ids; default EMPTY (off
//   everywhere) so rollout is an explicit per-environment opt-in,
//   certification by certification, beginning with PMP on staging.
//   Override: REACT_APP_METADATA_POOL_CERTS=pmp
//   NOTE (PR 1): the flag ships dark — nothing routes session starts through
//   the metadata pool yet. The hydration PR gates on isMetadataPoolEnabled().
//
// deferSessionExplanations (session-draw stage 3, PR 3)
//   When true, pool-routed session hydration EXCLUDES the explanation column;
//   explanations load on demand — per question at Practice submit, batched at
//   session end for results/review (telemetry: questions_load_review).
//   Independent of METADATA_POOL_CERTS (deferral only ever applies to
//   pool-hydrated sessions, but enabling/disabling it is its own decision).
//   Default: false. Override: REACT_APP_DEFER_SESSION_EXPLANATIONS=true

// journeyOnboardingEnabled
//   Gates the Journey Activation onboarding surface (first-time journey
//   panel, returning-dormant strip, and per-stage next-step recommendations
//   on the certification-select screen). Ships DARK: default false in every
//   environment; enabled per-environment for validation (deploy previews /
//   staging) via the env var. Do NOT enable in production before the
//   activation baseline gate (docs/activation-gate3-status.md).
//   Default: false. Override: REACT_APP_JOURNEY_ONBOARDING=true
//
// JOURNEY_DORMANT_DAYS (behavior config, not a flag)
//   Inactivity window, in days, before a confirmed learner with no observed
//   intent gets the returning-dormant treatment (and the strip's dismissal
//   suppression window). 14 is the initial hypothesis from the 2026-08
//   dormant-user analysis — expected to change once return behavior is
//   observed. Override: REACT_APP_JOURNEY_DORMANT_DAYS=<days>
//
function envBool(name, def) {
  // IMPORTANT: reference `process.env` directly. CRA's DefinePlugin replaces
  // the exact `process.env` token with an inlined object literal at build
  // time, but it does NOT rewrite `typeof process` — which is undefined in
  // the browser. The previous guard therefore skipped the inlined object and
  // silently pinned every flag to its default in production bundles.
  // Direct access works everywhere: browsers get the inlined literal, Node
  // (Jest, scripts) gets the real process.env.
  const v = process.env[name];
  if (v === undefined || v === null || v === '') return def;
  const s = String(v).toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

/**
 * Parses a comma-separated env list (same DefinePlugin caveat as envBool:
 * reference process.env directly). Empty/unset -> the default string.
 */
function envList(name, def) {
  const v = process.env[name];
  const raw = (v === undefined || v === null || v === '') ? def : String(v);
  return raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
}

export const FEATURE_FLAGS = {
  enablePmiPmocp: envBool('REACT_APP_ENABLE_PMI_PMOCP', true),
  deferSessionExplanations: envBool('REACT_APP_DEFER_SESSION_EXPLANATIONS', false),
  caseStudiesEnabled: envBool('REACT_APP_CASE_STUDIES_ENABLED', false),
  caseStudyAdminEnabled: envBool('REACT_APP_CASE_STUDY_ADMIN_ENABLED', false),
  caseStudiesInReadinessEnabled: envBool('REACT_APP_CASE_STUDIES_IN_READINESS', false),
  caseStudiesInExamsEnabled: envBool('REACT_APP_CASE_STUDIES_IN_EXAMS_ENABLED', false),
  journeyOnboardingEnabled: envBool('REACT_APP_JOURNEY_ONBOARDING', false),
  // Founding Tester program closure (2026-08): the program fulfilled its
  // purpose and enrollment is CLOSED. This flag gates only NEW participation
  // (auto-award on registration, welcome modal, recruitment CTAs). Existing
  // founder records, badges, the Founders Wall, the award email trigger on
  // admin grants, and all admin tooling are preserved regardless of this
  // flag. Reopen (unlikely): REACT_APP_FOUNDING_TESTER_ENROLLMENT=true
  foundingTesterEnrollmentOpen: envBool('REACT_APP_FOUNDING_TESTER_ENROLLMENT', false),
};

/**
 * Returns true if the named flag is enabled. An empty/undefined flag name is
 * treated as "always on" so config entries without a featureFlag are never
 * filtered out.
 * @param {string} [flag]
 * @returns {boolean}
 */
export function isFeatureEnabled(flag) {
  if (!flag) return true;
  return FEATURE_FLAGS[flag] !== false;
}

/**
 * Certs enabled for the metadata-pool session draw (session-draw stage 3).
 * Default: none. Staging opts in per cert via REACT_APP_METADATA_POOL_CERTS.
 */
export const METADATA_POOL_CERTS = envList('REACT_APP_METADATA_POOL_CERTS', '');

/**
 * True when the given certification may use the metadata-pool loader for
 * learner session draws. Unknown/empty cert ids are always false.
 * @param {string} [certId]
 * @returns {boolean}
 */
export function isMetadataPoolEnabled(certId) {
  return typeof certId === 'string'
    && certId.length > 0
    && METADATA_POOL_CERTS.includes(certId.toLowerCase());
}

/**
 * Parses a positive-integer env override (same DefinePlugin caveat: reference
 * process.env directly). Invalid / missing values fall back to the default.
 */
function envPosInt(name, def) {
  const v = process.env[name];
  if (v === undefined || v === null || v === '') return def;
  const n = Number.parseInt(String(v), 10);
  return Number.isFinite(n) && n > 0 ? n : def;
}

/**
 * Days of inactivity before the returning-dormant journey treatment applies.
 * Configurable behavior, not a product rule — see the header comment.
 */
export const JOURNEY_DORMANT_DAYS = envPosInt('REACT_APP_JOURNEY_DORMANT_DAYS', 14);

